import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';
import { loadFont as loadMontserrat } from '@remotion/google-fonts/MontserratAlternates';
import { loadFont as loadNoto } from '@remotion/google-fonts/NotoSansDevanagari';

const { fontFamily: mont } = loadMontserrat('normal', { weights: ['900'], subsets: ['latin'] });
const { fontFamily: noto } = loadNoto('normal', { weights: ['700'], subsets: ['devanagari'] });

const FONT_SIZE = 88;
const GOLD = '#FFD700';


// ── Shiva with moon — real vector from Maha Shivratri SVG ─────────────────────
const ShivaWithMoon: React.FC<{ opacity: number; glow: number }> = ({ opacity, glow }) => (
  <svg
    width="215"
    height="225"
    viewBox="750 400 860 900"
    fill="none"
    style={{ opacity }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      {/* Gold gradient across the Shiva silhouette in original coordinate space */}
      <linearGradient id="swm-gold" x1="0" y1="430" x2="0" y2="1300" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFFFF0" />
        <stop offset="35%" stopColor="#FFD700" />
        <stop offset="100%" stopColor="#C8860A" />
      </linearGradient>
      {/* Moon base — white/silver radial */}
      <radialGradient id="swm-g1" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
        gradientTransform="matrix(484.959,0,0,484.959,1213.484,790.959)">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset=".824" stopColor="#cccccc" />
        <stop offset=".99" stopColor="#ffffff" />
      </radialGradient>
      {/* Moon screen glow */}
      <radialGradient id="swm-g2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse"
        gradientTransform="matrix(425.488,0,0,425.488,1185.854,862.795)">
        <stop offset=".681" stopColor="#000000" />
        <stop offset="1" stopColor="#989898" />
      </radialGradient>
      <filter id="swm-glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation={1 + glow * 3} result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* Moon base circle */}
    <path
      d="m1611.34 862.79c0 235-190.49 425.49-425.49 425.49-234.99 0-425.48-190.49-425.48-425.49 0-234.99 190.49-425.48 425.48-425.48 235 0 425.49 190.49 425.49 425.48z"
      fill="url(#swm-g1)"
    />
    {/* Moon glow — screen blend */}
    <path
      d="m1611.34 862.79c0 235-190.49 425.49-425.49 425.49-234.99 0-425.48-190.49-425.48-425.49 0-234.99 190.49-425.48 425.48-425.48 235 0 425.49 190.49 425.49 425.48z"
      fill="url(#swm-g2)"
      style={{ mixBlendMode: 'screen' }}
    />

    {/* Shiva silhouette — recolored to gold */}
    <g fill="url(#swm-gold)" filter="url(#swm-glow)">
      <path d="m1535.05 1192.68c-15.04-8.89-49.36-8.1-81.18-12.99-31.83-4.89-112.66-14.9-122.46-15.78-9.8-0.87-9.8-0.87-9.8-0.87 0 0 42.68-58.89 54.51-81.77 11.83-22.88 13.65-34 9.15-59.33-4.49-25.33-23.11-87.27-25.21-108.86-2.1-21.58-2.1-72.12-39.45-88.98-37.36-16.85-38.32-7.95-39.82-16.14-1.51-8.2-5.99-34.27-9.21-37.29-3.22-3.01-3.22-3.01-3.22-3.01l-9.24-35.8-12.62-28.42c0 0-1-38.66-8.94-53.63-7.94-14.97-13.59-19.51-13.59-19.51 0 0 1.55-13.54-2.68-17.22-4.24-3.68-4.24-3.68-4.24-3.68 0 0 2.29-8.33-1.6-14.24-3.9-5.91-6.02-7.05-6.02-7.05 0 0 0.5-11.98-2.19-16.2-2.7-4.21-3.63-3.67-3.63-3.67 0 0 8.54-15.49-3.56-25.97-12.1-10.47-38.27-4.97-40 7.27-1.73 12.24 0.05 15.89 0.05 15.89 0 0-5.89 3.26-9.98 9.62-4.09 6.35-1.13 12.8-1.13 12.8 0 0-4.05 4.13-6.59 9.39-2.54 5.26 0.4 13.1 0.4 13.1 0 0-11.37 7.38-14.66 26.84-11.67 13.22-25.1 33.85-23.73 50.22 1.37 16.37 0.29 32.71-4.77 41.56-5.05 8.85 2.64 4.45-1.89 15.58-7.27 17.87-4.73 8.16-6.54 18.54-1.81 10.38-2.04 5.87-0.59 17.04 1.46 11.17 0.43 23.79-14.42 31.76-14.86 7.98-46.06 14.99-59.37 28.19-13.3 13.2-19.36 33.18-20.55 56.21-1.2 23.02-2.53 54.22-10.19 72.7-7.67 18.47-28.84 62.78-22.64 83.68 6.2 20.89 18.76 53.02 50.8 95.08 0 0-59.13 14.06-92.75 18.81-33.62 4.75-73.87 14.61-92.94 29.96-19.07 15.35-30.45 61.28-1.25 76.54 29.2 15.26 72.99 38.89 143.5 29.44 70.51-9.46 121.43-21.4 180.36-22.84 58.92-1.43 142.82 21.26 198.4 24.24 55.58 2.98 191.04-25.67 196.19-40.46 5.15-14.8 14.41-55.92-10.71-70.75zm-463.56-93.6c-8.86 16.98-10.99 22.15-14.5 11.33-3.5-10.83-20.55-44.13-24.88-54.22-4.33-10.1-4.76-8.99-0.47-21.19 4.29-12.21 32.13-63.49 34.39-51.94 1.79 9.15 10 38.79 13.35 59.27 3.34 20.49 0.98 39.78-7.89 56.75zm241.72-39.1c-4.66 9.97-22.79 42.8-26.64 53.53-3.86 10.72-5.82 5.49-14.13-11.71-8.31-17.21-10.04-36.56-6.02-56.94 4.01-20.39 13.19-49.79 15.28-58.88 2.64-11.49 28.79 40.52 32.68 52.84 3.89 12.31 3.5 11.19-1.17 21.16z" />
      <path d="m1272.16 800.76c5.85 6.03 22.25 2.56 23.54-8.94 2.15-19.22-14.08-28.08-16.46-42.61-3.22-19.6 8.94-26.25 21.25-29.41 12.31-3.16 15.25-1.26 15.2 2.08-0.05 3.35-4.9 5.13-4.9 5.13 0 0-5.22 0.67-2.2 22.64 3.02 21.97 8.06 57.72-14.32 63.33-22.38 5.62-26.74-0.77-26.74-0.77z" />
      <path d="m1249.09 629.48c-13.8-0.98-24.19-12.95-23.21-26.75 0.65-9.14 6.14-16.76 13.77-20.59-13.58 0.98-24.73 11.78-25.72 25.75-1.09 15.33 10.45 28.64 25.78 29.73 10.35 0.73 19.77-4.3 25.13-12.37-4.47 3.03-9.95 4.65-15.75 4.23z" />
      <path d="m1349.93 926.45c15.57-14.29 32.17-53.64 36.65-65.29 4.49-11.64 0.47-28.42 0.47-28.42 0 0-10.86-11.29-11.92-21.34-1.07-10.05-1.52-20.09-1.52-20.09 0 0-8.6 1-9.11-5.14-0.52-6.14 0.23-14.49 6.99-14.96 0 0-1.05-11.16 10.64-12.67 11.68-1.52 38.02 1.07 47.74 7.33 9.71 6.26 10.4 39.16 4.41 67.51-5.98 28.35-11.98 57.25-15.28 71.7-3.31 14.45-17.17 108.13-56 118.76-38.83 10.62-13.07-97.39-13.07-97.39z" />
      <path d="m1016.11 921.82c-0.73-11.13-25.63-46.06-27.84-61.14-2.21-15.08-0.95-16.74 7.83-28.32 8.78-11.59 11.51-28.84 11.69-39.98 0.19-11.15 0.54-32.88-11.15-30.81-11.69 2.07-9.89 4.33-9.89 4.33 0 0-6.66-5.11-11.57-5.18-4.91-0.06-11.15 6.54-11.15 6.54 0 0-12.77-6.86-16.47-5.8-3.7 1.07-5.05 8.3-5.05 8.3 0 0-0.06 41.25-4.02 58.47-3.97 17.23 1.27 34.58 3.41 54.12 2.13 19.54 20.76 81.11 43.88 93.13 23.12 12.01 31.31-38.6 30.33-53.66z" />
      <path d="m976.42 775.74l-70.2-13.35c0 0-7.48 18.17-7.09 37.76 0.4 19.59 4.56 28.79 4.56 28.79l71.91-3.6 45.88 6.49c0 0 12.57-16.39 10.52-56.7-0.09-1.76-1.78-3.1-3.71-2.97z" />
      <path d="m990.18 860.53l-52.52 6.92c-3.52 0.47-6.79-1.75-7.3-4.95l-0.09-0.55c-0.51-3.2 1.93-6.17 5.45-6.63l52.53-6.93c3.52-0.46 6.79 1.75 7.3 4.95l0.09 0.55c0.51 3.2-1.93 6.17-5.46 6.64z" />
      <path d="m1428.79 861.7l-41.6-3.67c-3.65-0.33-6.32-3.28-5.97-6.59l0.02-0.2c0.36-3.31 3.6-5.74 7.25-5.42l41.6 3.67c3.65 0.32 6.32 3.27 5.97 6.59l-0.02 0.19c-0.35 3.32-3.6 5.75-7.25 5.43z" />
      <path d="m1044.81 1010.46l-69.28-18.92c-3.99-1.09-6.25-4.91-5.05-8.53 1.2-3.63 5.41-5.68 9.39-4.59l69.28 18.91c3.99 1.09 6.25 4.91 5.05 8.54-1.2 3.62-5.4 5.68-9.39 4.59z" />
      <path d="m1373.08 999.1l-69.84 17.11c-4.02 0.98-8.16-1.18-9.24-4.83-1.09-3.66 1.29-7.42 5.32-8.4l69.84-17.11c4.02-0.98 8.16 1.18 9.24 4.84 1.09 3.65-1.29 7.41-5.32 8.39z" />
      <path d="m915.36 891.86c-2.42-10.17-1.17-31.19 12.23-47.41 10.71-12.98 11.2-19.13 11.67-25.07 0.08-1.08 0.17-2.15 0.3-3.24l2.61 0.34c-0.13 1.05-0.21 2.07-0.29 3.1-0.5 6.36-1.02 12.93-12.27 26.55-12.77 15.48-13.99 35.46-11.7 45.12z" />
      <path d="m922.46 891.55c0 2.93-2.61 5.3-5.82 5.3-3.22 0-5.83-2.37-5.83-5.3 0-2.92 2.61-5.29 5.83-5.29 3.21 0 5.82 2.37 5.82 5.29z" />
      <path d="m1460.15 643.76c-5.19-11.42-20.45-32.32-22.15-57.98-0.69-10.09 2.04-26.14 10.71-31.31-12.72 3-26.6 30.75-12.64 68.4 6.86 19 25.34 44.81 9.62 58.44-10.79 9.58-34.5 4.56-32.65-11.44 0.95-8.41 6.56-11.91 13.5-10.29 2.65 0.59 4 2.37 4.98 0.68q0.18-0.3 0.19-0.61c0.15-2.2-5.06-4.57-7.6-5.21-6.14-1.49-11.35 0.58-14.97 4.52l6.15-15.09c-3.22-10.57-6.01-29.64-8.42-52.2q-0.81-7.53-1.56-15.5c-1.24-13.27-2.37-27.26-3.4-41.15v0.01q0-0.01 0-0.01c-3.23 44.14-6.84 87.84-13.37 108.85l6.13 15.04c-3.62-3.9-8.81-5.96-14.93-4.47-2.54 0.64-7.75 3.01-7.6 5.21q0.01 0.31 0.19 0.61c0.98 1.69 2.32-0.09 4.98-0.68 6.94-1.62 12.54 1.88 13.5 10.29 1.85 16-21.86 21.02-32.66 11.44-15.71-13.63 2.77-39.44 9.63-58.44 13.96-37.65 0.08-65.4-12.64-68.4 8.67 5.17 11.4 21.22 10.71 31.31-1.7 25.66-16.96 46.56-22.15 57.98-7.99 16.03-8.29 36.73 5.63 48.1 13.67 11.1 37.65 10.1 47.44-5.85-0.53 0.87-0.86 1.89-0.86 2.99 0 1.8 0.85 3.39 2.16 4.45v497.97h7.69v-498.42c1.01-1.03 1.63-2.44 1.63-4 0-1.56-0.62-2.97-1.63-4.01v-1.3q0.02-0.01 0.05-0.02 0.48 1.05 1.07 2.06c9.68 16.23 33.88 17.3 47.63 6.13 13.93-11.37 13.63-32.07 5.64-48.1zm-63.13 41.88q0.5-0.86 0.91-1.74 0.07 0.04 0.14 0.07v0.58q-0.6 0.47-1.05 1.09z" />
      <path d="m1408.15 1203.68h-11.89v-521.64h11.89z" />
    </g>
  </svg>
);

// ── Lotus (from MantraCardBadrinath1) ──────────────────────────────────────────
const Lotus: React.FC<{ cx: number; cy: number; r: number; startFrame: number; uid: string }> = ({ cx, cy, r, startFrame, uid }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relFrame = frame - startFrame;
  if (relFrame < 0) return null;
  const sc = spring({ fps, frame: Math.max(0, relFrame), config: { damping: 13, stiffness: 110, mass: 0.8 }, durationInFrames: 18 });
  const pulse = 1 + 0.035 * Math.sin(frame * 0.18);
  return (
    <g transform={`translate(${cx}, ${cy}) scale(${sc * pulse})`}>
      <defs>
        <linearGradient id={`lg-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFFFF0" /><stop offset="45%" stopColor={GOLD} /><stop offset="100%" stopColor="#C8860A" />
        </linearGradient>
        <filter id={`lf-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {Array.from({ length: 8 }).map((_, i) => (
        <ellipse key={i} cx={0} cy={-r * 0.55} rx={r * 0.24} ry={r * 0.6}
          fill={`url(#lg-${uid})`} stroke="rgba(255,200,0,0.4)" strokeWidth={0.5} opacity={0.88}
          transform={`rotate(${i * 45})`} />
      ))}
      {Array.from({ length: 4 }).map((_, i) => (
        <ellipse key={i} cx={0} cy={-r * 0.22} rx={r * 0.17} ry={r * 0.32}
          fill="#FFFDE0" opacity={0.92} transform={`rotate(${i * 90 + 22.5})`} />
      ))}
      <circle cx={0} cy={0} r={r * 0.12} fill="#FFF8C0" filter={`url(#lf-${uid})`} />
    </g>
  );
};

// ── Trishul (paths from HarHarMahadevCTA, scaled to fit) ──────────────────────
const TrishulCenter: React.FC<{ cx: number; cy: number; scale: number; startFrame: number; glow: number }> = ({ cx, cy, scale, startFrame, glow }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relFrame = frame - startFrame;
  if (relFrame < 0) return null;
  const sc = spring({ fps, frame: Math.max(0, relFrame), config: { damping: 14, stiffness: 100, mass: 0.9 }, durationInFrames: 22 });
  const pulse = 1 + 0.025 * Math.sin(frame * 0.2);
  // Trishul paths are in a 203x392 viewBox; we centre it at origin then translate
  const tx = cx - (203 * scale) / 2;
  const ty = cy - (392 * scale) / 2;
  return (
    <g transform={`translate(${tx}, ${ty}) scale(${scale * sc * pulse})`} filter={`url(#tglow-js)`}>
      <defs>
        <linearGradient id="tgold-js" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#B8860B" />
          <stop offset="35%" stopColor={GOLD} />
          <stop offset="65%" stopColor="#FFF5A0" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
        <filter id="tglow-js">
          <feGaussianBlur stdDeviation={1 + glow * 2} result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g fill="url(#tgold-js)">
        <path d="M33.499,144.251c3.662-1.943,9.738-1.436,14.772,0.443c10.396-18.138,17.586-81.09-7.309-95.717c15.417,42.394-6.783,76.49-8.583,79.536l-1.114,1.678l-0.77,1.2C32.434,135.627,33.327,139.967,33.499,144.251z" />
        <path d="M152.59,143.94c4.398-0.919,9.115-1.308,12.162,0.308c0.21-5.238,1.496-11.057,4.456-15.503l-0.154-0.232c-1.802-3.046-24-37.142-8.584-79.536c-25.053,14.719-18.396,77.261-7.884,94.954L152.59,143.94z" />
        <path d="M99.301,0.088c-0.021,13.632-5.121,89.769-19.03,119.995l-0.816,1.81c0.324-0.011,1.201-0.07,1.525-0.07c6.682,0.015,13.681,2.289,18.766,7.109c4.964-4.706,12.894-6.789,19.436-6.906l-0.848-1.942C104.425,89.857,99.322,13.72,99.301,0.088z" />
        <path d="M192.981,205.221c-0.852-2.601-3.635-17.944-6.557-31.677c-1.246-5.87-13.908-44.277-0.488-44.313c-13.692-8.271-18.371,8.438-18.79,18.87c-4.964-2.634-14.366-2.616-19.591,2.38c-2.824,2.701-3.393,9.954-1.215,12.029c6.166-11.215,15.965,6.552,18.42,11.946c4.434,9.729,6.271,32.455-3.207,39.996c-12.093,9.625-30.44,1.15-44.426-10.643l-9.499,5.417c0.391,1.834,0.606,3.822,0.608,5.908c-0.004,4.316-0.905,8.235-2.439,11.177c-0.214,0.407-0.444,0.796-0.687,1.167c-0.055,0.084-0.113,0.16-0.169,0.242c-0.202,0.294-0.41,0.578-0.63,0.844c-0.055,0.065-0.11,0.128-0.166,0.192c-0.242,0.278-0.493,0.542-0.757,0.782c-0.036,0.034-0.073,0.068-0.109,0.102c-0.308,0.271-0.626,0.52-0.959,0.732h0.001c-0.948,0.607-1.998,0.972-3.15,0.983c-1.115-0.011-2.136-0.349-3.06-0.92l0,0c-0.337-0.208-0.659-0.454-0.969-0.723c-0.06-0.052-0.117-0.107-0.175-0.16c-0.247-0.226-0.484-0.469-0.712-0.729c-0.073-0.082-0.146-0.165-0.216-0.251c-0.21-0.254-0.411-0.524-0.604-0.806c-0.065-0.094-0.133-0.184-0.197-0.28c-0.244-0.375-0.478-0.766-0.693-1.177c-1.535-2.941-2.436-6.86-2.44-11.177c0.002-2.353,0.278-4.578,0.771-6.6l-9.19-5.191c-14.018,12.034-32.645,20.859-44.896,11.108c-9.475-7.541-7.639-30.267-3.207-39.996c2.457-5.394,12.255-23.161,18.421-11.946c2.176-2.075,1.608-9.328-1.217-12.029c-5.224-4.997-14.626-5.014-19.589-2.38c-0.42-10.432-5.1-27.141-18.791-18.87c13.418,0.036,0.758,38.443-0.491,44.313c-2.921,13.733-5.705,29.076-6.556,31.677c-0.908,2.77-1.59,4.088-5.361,7.082c2.862,3.139,4.91,7.863,5.816,9.951c2.866,6.609,2.959,29.955,20.896,37.252c32.638,13.279,50.169-27.006,59.392-28.898c5.418,15.303-10.312,33.072-10.312,33.072l6.433,4.622c0,0,16.885,81.185-6.433,118.187c-11.437,18.147,57.514,16.35,46.936,0c-23.618-36.502-6.433-118.187-6.433-118.187l6.433-4.251c0,0-15.393-18.181-10.144-33.523c10.059,3.403,30.145,41.37,59.047,28.979c17.797-7.633,18.028-30.643,20.896-37.25c0.905-2.09,2.955-6.814,5.815-9.953C194.572,209.311,193.889,207.993,192.981,205.221z" />
        <path d="M99.172,134.413c-10.139-14.514-39.866-11.587-36.586,9.843c18.671-15.452,35.253,19.54,18.406,31.885c3.329,0.551,12.355,11.666-3.736,20.307l14.919,8.288c0.121-0.263,0.24-0.528,0.37-0.777c1.52-2.907,3.774-5.019,6.627-5.046c2.85,0.027,5.105,2.139,6.625,5.046c0.166,0.318,0.321,0.653,0.473,0.994l14.527-8.504c-15.021-10.12-6.777-19.756-3.447-20.307c-16.846-12.345-0.266-47.337,18.406-31.885C139.036,122.826,109.309,119.899,99.172,134.413z" />
      </g>
    </g>
  );
};

// ── Sparkle ────────────────────────────────────────────────────────────────────
const Sparkle: React.FC<{ cx: number; cy: number; r: number; phase: number }> = ({ cx, cy, r, phase }) => {
  const frame = useCurrentFrame();
  const t = (frame * 1.2 + phase) % 64;
  const op = interpolate(t, [0, 10, 32, 54, 64], [0, 0.8, 0.35, 0.8, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });
  const sc = interpolate(t, [0, 10, 32, 54, 64], [0.5, 1.1, 0.8, 1.1, 0.5], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });
  const s = r * sc;
  return (
    <polygon
      points={`${cx},${cy - s * 2.2} ${cx + s * 0.45},${cy - s * 0.45} ${cx + s * 2.2},${cy} ${cx + s * 0.45},${cy + s * 0.45} ${cx},${cy + s * 2.2} ${cx - s * 0.45},${cy + s * 0.45} ${cx - s * 2.2},${cy} ${cx - s * 0.45},${cy - s * 0.45}`}
      fill="#FFF8C0"
      opacity={op}
    />
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────
export const JaiSomnathCTA: React.FC<{ durationInFrames?: number }> = ({ durationInFrames: durProp }) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames: compDur } = useVideoConfig();
  const dur = durProp ?? compDur;

  const fadeOutStart = dur - Math.round(fps * 1.5);
  const globalOpacity = interpolate(frame, [fadeOutStart, dur - 1], [1, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  const eaz = Easing.bezier(0.4, 0, 0.2, 1);
  const WIPE = 18;

  const jayStart     = 6;
  const somnathStart = jayStart + Math.round(fps * 0.5);

  const jayClip     = interpolate(frame, [jayStart,     jayStart     + WIPE], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });
  const somnathClip = interpolate(frame, [somnathStart, somnathStart + WIPE], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });

  const logoGlow = Math.sin(frame * 0.15) * 0.35 + 0.65;

  // Shiva entrance — slight delay after jyotirlinga
  const shivaSc  = spring({ fps, frame: Math.max(0, frame - 4), config: { damping: 13, stiffness: 90, mass: 1.0 }, durationInFrames: 22 });
  const shivaOp  = interpolate(frame, [4, 20], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const lineW  = interpolate(frame, [somnathStart + WIPE, somnathStart + WIPE + 16], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });
  const hindiOp = interpolate(frame, [somnathStart + WIPE + 8, somnathStart + WIPE + 22], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const decoStart   = somnathStart + WIPE + 10;
  const lotusFrame  = decoStart;
  const trishulFrame = decoStart + 8;
  const trishulGlow = Math.sin(frame * 0.2) * 0.3 + 0.7;

  const textStyle: React.CSSProperties = {
    fontSize: FONT_SIZE,
    fontWeight: 900,
    fontFamily: mont,
    color: '#fff',
    letterSpacing: 12,
    lineHeight: 1,
    textShadow: `0 0 60px rgba(255,215,0,0.25)`,
  };

  return (
    <AbsoluteFill style={{ opacity: globalOpacity, backgroundColor: 'transparent' }}>

      {/* Centre column */}
      <AbsoluteFill style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 }}>

        {/* Shiva with moon — centred above text */}
        <div style={{ transform: `scale(${shivaSc})`, transformOrigin: 'center bottom', marginBottom: 10, opacity: shivaOp }}>
          <ShivaWithMoon opacity={1} glow={logoGlow} />
        </div>

        {/* JAI  SOMNATH */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 28, marginTop: 8 }}>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ ...textStyle, clipPath: `inset(0 ${100 - jayClip}% 0 0)` }}>JAI</div>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ ...textStyle, clipPath: `inset(0 ${100 - somnathClip}% 0 0)` }}>SOMNATH</div>
          </div>
        </div>

        {/* Gold underline */}
        <div style={{
          width: `${lineW * 6.8}px`,
          maxWidth: 820,
          height: 2,
          background: 'rgba(255,215,0,0.75)',
          marginTop: 10,
        }} />

        {/* Hindi */}
        <div style={{
          opacity: hindiOp,
          color: 'rgba(255,215,0,0.85)',
          fontSize: 42,
          fontFamily: noto,
          fontWeight: 700,
          letterSpacing: 2,
          marginTop: 12,
        }}>
          जय सोमनाथ
        </div>

        <div style={{ height: 100 }} />
      </AbsoluteFill>

      {/* SVG layer — sparkles + Lotus · Trishul · Lotus */}
      <AbsoluteFill style={{ pointerEvents: 'none' }}>
        <svg width={1280} height={704} viewBox="0 0 1280 704" xmlns="http://www.w3.org/2000/svg">
          <Sparkle cx={80}   cy={100} r={4} phase={0}  />
          <Sparkle cx={1200} cy={110} r={4} phase={20} />
          <Sparkle cx={90}   cy={580} r={3} phase={35} />
          <Sparkle cx={1190} cy={570} r={3} phase={12} />
          <Sparkle cx={380}  cy={80}  r={3} phase={8}  />
          <Sparkle cx={900}  cy={75}  r={3} phase={28} />

          {/* Lotus · Trishul · Lotus — mirroring MantraCardBadrinath1 layout */}
          <Lotus cx={490} cy={562} r={40} startFrame={lotusFrame}     uid="jsl" />
          <TrishulCenter cx={640} cy={548} scale={0.28} startFrame={trishulFrame} glow={trishulGlow} />
          <Lotus cx={790} cy={562} r={40} startFrame={lotusFrame + 6} uid="jsr" />
        </svg>
      </AbsoluteFill>

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
